import React, { useState, useRef, useEffect } from 'react';
import { SearchInput } from './search_input';
import { navigateOnSubmit } from './submit_navigation';
import { SearchResults } from './search_results';
import { SearchPagination } from './search_pagination';
import { ResponsiveStyles, InstanceStyles, INLINE_DROPDOWN_CSS, INLINE_RESULTS_CSS, PAGINATION_CSS, SEARCH_INPUT_CSS, SHADOW_SURFACE_RESET_CSS, THEME_VARIANT_CSS } from './responsive_styles';

const DEFAULT_INLINE_MAX = 5;

// capInlineResults limits the inline dropdown to at most `max` hits (default 5,
// used when max is missing or not a positive number) without mutating the
// source results object. Multi-mode widget (#5414).
export function capInlineResults(results, max) {
  const limit = Number.isFinite(max) && max > 0 ? Math.floor(max) : DEFAULT_INLINE_MAX;
  const hits = Array.isArray(results?.hits) ? results.hits.slice(0, limit) : [];
  return { ...(results || {}), hits };
}

// eventStartedInside reports whether an event originated at or within `el`.
//
// `el.contains(e.target)` is the obvious spelling and it is wrong for this
// widget: every surface mounts inside a shadow root, and an event crossing that
// boundary is retargeted, so a listener on `document` sees `e.target` as the
// shadow HOST rather than the element clicked. A container living inside that
// shadow tree does not contain its own host, so every mousedown within the
// widget reads as an outside click. The pager was the visible casualty -- the
// dropdown dismissed before the button's click could fire (item #16333).
//
// composedPath() carries the pre-retarget path, host and all. Fall back to
// contains() where it is unavailable, which is the light-DOM case the original
// check already handled correctly.
export function eventStartedInside(e, el) {
  if (!el) return false;
  const path = typeof e?.composedPath === 'function' ? e.composedPath() : null;
  if (path && path.length > 0) return path.includes(el);
  return el.contains(e?.target ?? null);
}

// InlineSearch renders a search input with an absolutely-positioned results
// dropdown beneath it. The dropdown opens on focus/typing and closes on
// outside-click, Escape, or focus leaving the widget. Results are capped at
// merged.inlineMaxResults. Multi-mode widget (#5414).
//
// This is the attached surface: the results stay anchored to the bar. The
// layout says how they are presented there -- a list, or the tile cards
// (decision #4963). Nothing about the anchoring changes with the layout.
export function InlineSearch({ search, merged = {}, enabled, demoMode, demoTransport = null, onResultClick, styleVars = {} }) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    const onDocMouseDown = (e) => {
      if (containerRef.current && !eventStartedInside(e, containerRef.current)) {
        setOpen(false);
      }
    };
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDocMouseDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, []);

  // Close when focus leaves the whole inline widget (keyboard tab-out). A focus
  // move to a child (e.g. the clear button) keeps it open.
  const handleBlur = (e) => {
    if (containerRef.current && !containerRef.current.contains(e.relatedTarget)) {
      setOpen(false);
    }
  };

  // Alt+ArrowLeft/Right pages the results, the one keyboard affordance The
  // Diff's bespoke bundle had that the standard widget did not (item #16246).
  //
  // Alt+Left/Right is browser back/forward on Windows and Linux, so this either
  // acts AND consumes the event or does neither -- swallowing a shortcut the
  // widget has no use for is worse than not binding it. Every fall-through path
  // below leaves the event to the browser: pagination off, dropdown closed, or
  // already at the first/last page.
  //
  // Scoped to the container rather than the document, unlike the Escape handler
  // above: Escape dismisses, which is a reasonable thing to do from anywhere on
  // the page, while paging is only meaningful to a visitor looking at these
  // results. The bare arrow keys are deliberately untouched -- the input is
  // inside this container and they move the caret.
  const handleKeyDown = (e) => {
    if (!e.altKey) return;
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
    if (!open || !merged.enablePagination) return;

    const forward = e.key === 'ArrowRight';
    const canMove = forward ? search?.has_next_page?.() : search?.has_previous_page?.();
    if (!canMove) return;

    e.preventDefault();
    search.set_page(search.page + (forward ? 1 : -1));
  };

  const cappedResults = capInlineResults(search?.results, merged.inlineMaxResults);

  // Whether opening the dropdown would show the visitor anything (item #16443).
  //
  // use_search.js skips the empty query when showDefaultResults is false, which
  // is correct -- it is the customer's instruction not to query on an empty box.
  // search_results.jsx then renders "No results found" for the empty set, which
  // is also correct: that is the right message for a search that ran and
  // returned nothing. Neither is wrong on its own, and nothing between them
  // asked whether a search had run at all, so an untouched bar claimed the
  // index was empty -- on mount, with autoFocus (decision #5602).
  //
  // Derived rather than folded into the onFocus handler because there are two
  // ways in: focusing the bar, and typing. Gating only focus leaves deleting a
  // query back to empty re-opening on the same empty state.
  const hasSomethingToShow = Boolean(search?.query) || Boolean(merged.showDefaultResults);

  // The root's style attribute carries ONLY the custom properties
  // themeStyleVars computes. `position: relative` and `width: 100%` were static
  // values in it until item #17191 and now live in INLINE_DROPDOWN_CSS, which is
  // where the rest of this surface's geometry is declared -- and, for position,
  // where the panel that consumes it is declared too. An inline style beats
  // every rule in these blocks (specificity 0 under :where(), decision #5090),
  // so spending it on a static is item #16444's defect one element up.
  return (
    <div
      className="vo-search-inline"
      ref={containerRef}
      data-theme={merged.theme || 'light'}
      style={styleVars}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
    >
      { /* The reset goes first: it is specificity 0 like the mirrors below it,
           so only source order keeps their margin rules from being flattened
           by its `margin: 0`.

           SEARCH_INPUT_CSS follows it. This host is the reason that constant
           exists: it renders SearchInput but not INPAGE_SURFACE_CSS, where the
           input family used to live, so inside a shadow root nothing declared
           the input's background or colour and the UA white/black won over a
           theme that computed and inherited correctly (decision #5090). In the
           light DOM vo-search.css supplied the rules, which is why this only
           surfaced once the surface moved into a shadow root. */ }
      <ResponsiveStyles css={SHADOW_SURFACE_RESET_CSS} />
      <ResponsiveStyles css={SEARCH_INPUT_CSS} />
      <ResponsiveStyles css={INLINE_DROPDOWN_CSS} />
      <ResponsiveStyles css={INLINE_RESULTS_CSS} />
      <ResponsiveStyles css={PAGINATION_CSS} />
      { /* LAST, after every mirror it overrides -- the same contract the
           overlay's ScopedOverlayStyles follows. Every block here is
           :where()-wrapped at specificity 0, so source order is the whole
           cascade: rendered before SEARCH_INPUT_CSS the themed 1px glass
           wrapper border would lose to the base 2px accent border and the bar
           would keep looking nothing like the panel. */ }
      <ResponsiveStyles css={THEME_VARIANT_CSS} />
      { /* After every mirror, the theme variants included: the instance's own
           stylesheet is the final word on how this bar looks (item #17981). */ }
      <InstanceStyles css={merged.customCss} />
      { /* onSubmit is the THIRD path into setOpen (item #16450), alongside
           focusing the bar and typing, and it is deliberately spelled the same
           way as those two.

           A first cut wrote `setOpen(hasSomethingToShow)`, reasoning that a
           submit on an empty box must not re-open the panel #16443 closed. It
           must not, and it does not -- but the gate that stops it is the render
           condition below, `open && hasSomethingToShow`, not the setter. Gating
           the setter as well changed nothing observable (a mutation back to
           setOpen(true) left the whole suite green) and made one of three
           identical paths read as if it carried a rule the other two did not.
           The gate belongs in one place; this is not that place.

           Item #17172 gave submit a SECOND possible outcome, and the two are
           alternatives rather than a sequence: an instance that names a results
           destination navigates, and opening a panel on a page that is leaving
           is a flash of suggestions the visitor cannot use. navigateOnSubmit
           reports whether it consumed the submit, so the fall-through is
           #16450's behaviour, unchanged, for every instance that names none. */ }
      <SearchInput
        query={search.query}
        onQueryChange={(v) => { search.set_query(v); setOpen(true); }}
        onClear={search.clear_search}
        onFocus={() => setOpen(true)}
        autoFocus={merged.autoFocus}
        primaryColor={merged.primaryColor}
        inputIconColor={merged.inputIconColor}
        placeholder={merged.searchPlaceholder}
        demoMode={demoMode}
        demoTransport={demoTransport}
        showInputIcon={merged.showInputIcon}
        showClearButton={merged.showClearButton}
        submitButtonEnabled={merged.submitButtonEnabled}
        submitButtonLabel={merged.submitButtonLabel}
        submitButtonIcon={merged.submitButtonIcon}
        onSubmit={() => { if (!navigateOnSubmit(merged, search.query)) setOpen(true); }}
        dropdownAnchor={merged.dropdownAnchor}
      >
      { /* Rendered INSIDE the bar, not beside it (item #17191). The panel is
           absolutely positioned at `top: 100%; left: 0; right: 0`, which says
           "the bottom edge and the width of my containing block" -- so it is
           only the bar's extent if the bar is the containing block. It was a
           sibling of the bar until this item, resolving against this root
           instead, and landed in the right place only because the root, the
           bar and its wrapper were all width: 100%. INLINE_DROPDOWN_CSS
           declares the anchor; this is the half that makes the declaration
           true. */ }
      {open && hasSomethingToShow && enabled && search && (
        <div className="vo-search-inline-dropdown">

          <SearchResults
            results={cappedResults}
            isLoading={search.is_loading}
            error={search.error}
            onResultClick={onResultClick}
            primaryColor={merged.primaryColor}
            displayTags={merged.displayTags}
            variant={merged.shape?.layout === 'grid' ? 'tile' : 'list'}
            tileColumns={merged.tileColumns}
            showHeroImage={merged.showHeroImage}
            showExcerpts={merged.showExcerpts}
            excerptLines={merged.excerptLines}
            showThumbnails={merged.showThumbnails}
            showAccessIcons={merged.showAccessIcons}
            openInNewTab={merged.openInNewTab}
          />
          {merged.enablePagination && (
            <SearchPagination
              page={search.page}
              hasPreviousPage={search.has_previous_page?.()}
              hasNextPage={search.has_next_page?.()}
              onPreviousPage={() => search.set_page(search.page - 1)}
              onNextPage={() => search.set_page(search.page + 1)}
              paginationButtonBgColor={merged.paginationButtonBgColor}
            />
          )}
        </div>
      )}
      </SearchInput>
    </div>
  );
}
